<script setup lang="ts">
import { ref, computed } from 'vue';
import { useQuasar } from 'quasar';
import { formatDateTime } from '@/functions/formatDate';
import type { StudentCommentResponse } from '@/types/Response/StudentCommentResponse';
import { useUserStore } from '@/stores/user';
import { useSchoolStore } from '@/stores/school';
import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import StudentCommentRepository from '@/repositories/StudentCommentRepository';
import type FlightSchool from '@/types/FlightSchool';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import DeleteCommentDialog from '@/components/Student/Comments/DeleteCommentDialog.vue';

const props = defineProps<{
    comments: StudentCommentResponse[],
    loading: boolean,
    student: FlightSchoolCourseStudent
}>();

const numPerPage = 2;
const currentPage = ref(1);
const maxPages = computed(() => (props.comments ? Math.ceil(props.comments.length / 2) : 0));
const userStore = useUserStore();
const { isGlobalAdmin } = userStore;
const emit = defineEmits(['update:comments']);

const getTimeString = (seconds: number) => {
    if (seconds === 0) {
        return 'N/A';
    }

    const sign = seconds >= 0 ? '+' : '-';

    const hours = Math.floor(Math.abs(seconds) / 3600);
    const hourString = hours === 0 ? '' : `${hours} hour${hours > 1 ? 's' : ''} `;

    const minutes = Math.abs(seconds) / 60 - hours * 60;
    const minuteString = minutes === 0 ? '' : `${minutes} minute${minutes > 1 ? 's' : ''}`;

    return `${sign}${hourString}${minuteString}`;
};

// Deleting Comments
const $q = useQuasar();
const buttonLoading = ref(false);

const deleteComment = async (commentId: number) => {
    buttonLoading.value = true;

    await StudentCommentRepository.deleteStudentComment(
        useSchoolStore().school as FlightSchool,
        useSchoolStore().course as FlightSchoolCourse,
        props.student,
        commentId,
    ).then(() => {
        $q.notify({
            color: 'green-5',
            textColor: 'white',
            icon: 'check',
            message: 'Comment deleted successfully',
        });
    }).catch(() => {
        $q.notify({
            color: 'red-5',
            textColor: 'white',
            icon: 'warning',
            message: 'Something went wrong',
        });
    }).finally(() => {
        buttonLoading.value = false;
        emit('update:comments');
    });
};

const promptDeleteComment = (commentId: number) => {
    $q.dialog({
        component: DeleteCommentDialog,
        componentProps: {
            loading: buttonLoading.value,
        },
    }).onOk(() => {
        deleteComment(commentId);
    });
};
</script>
<template>
    <q-card
        flat
        class="q-my-auto q-mx-auto q-pa-md bg-white comment-list"
    >
        <div class="header q-pl-md q-pb-sm text-weight-bold">
            Previous Comments
        </div>
        <q-list
            v-if="comments.length"
            separator
        >
            <q-item
                v-for="(comment, index) in comments"
                v-show="index >= (currentPage - 1) * numPerPage && index < currentPage * numPerPage"
                :key="index"
                class="q-pa-md comment"
                :class="(index + 1) % numPerPage !== 0 ? 'no-separator' : ''"
                no-wrap
            >
                <q-item-section top>
                    <q-item-label class="text-weight-bold comment-header">
                        {{ comment.adminName }} | {{ formatDateTime(comment.createdAt) }}
                    </q-item-label>
                    <q-item-label class="text-weight-bold comment-header q-pb-md">
                        Time Adjustment: {{ getTimeString(comment.timeSpent) }}
                    </q-item-label>
                    <q-item-label class="comment-body">
                        <q-scroll-area style="height: 125px;">
                            {{ comment.comment }}
                        </q-scroll-area>
                    </q-item-label>
                    <q-item-label
                        v-if="isGlobalAdmin || comment.adminCustomerId === userStore.user?.id"
                        class="comment-footer"
                    >
                        <q-btn
                            label="Delete Comment"
                            :ripple="false"
                            class="anchor-button text-body2 q-pl-none q-mt-md delete-btn"
                            color="fsum-blue"
                            no-caps
                            dense
                            flat
                            align="left"
                            @click="promptDeleteComment(comment.id)"
                        />
                    </q-item-label>
                </q-item-section>
            </q-item>
        </q-list>
        <div
            v-else
            class="q-pl-md"
        >
            No comments available
        </div>
        <div class="q-pa-lg flex flex-center">
            <q-pagination
                v-if="comments.length"
                v-model="currentPage"
                :max="maxPages"
                input
                color="fsum-med-blue"
            />
        </div>
        <q-inner-loading
            :showing="loading"
        />
    </q-card>
</template>
<style scoped lang="scss">
.comment-list {
    color: var(--fsum-black);

    .header {
        font-size: 32px;
    }

    .comment {
        min-height: 225px;
        max-height: max-content;
        font-size: 14px;

        div.comment-header, div.comment-body, div.comment-footer {
            height: auto;
        }
    }

    :deep(.no-separator) {
        border-top: none;
    }
}
</style>
